import { useState, useEffect } from "react";
import { boqAPI } from "../utils/api";

const CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Pune", "Kolkata",
  "Ahmedabad", "Nagpur", "Jaipur", "Indore", "Bhopal", "Surat", "Lucknow",
  "Coimbatore", "Visakhapatnam", "Nashik", "Vadodara", "Patna", "Other / Rural",
];

const QUALITY_INFO = {
  economy: { label: "Economy / Budget", color: "#2D7A4A", rate: "₹1,400/sqft", desc: "M20 concrete, local bricks, basic ceramic tiles, standard CPVC." },
  standard: { label: "Standard Quality", color: "#2563EB", rate: "₹1,850/sqft", desc: "M20/M25 mix, Fe500D TMT, vitrified tiles, Jaquar/Cera fittings, Asian paint." },
  premium: { label: "Premium Quality", color: "#E8571A", rate: "₹2,600/sqft", desc: "M25 concrete, Tata Tiscon steel, double charged tiles/granite, UPVC windows." },
  luxury: { label: "Ultra Luxury", color: "#7C3AED", rate: "₹3,800/sqft", desc: "Italian marble flooring, VRV AC, smart home automation, solid teak woodwork." },
};

export default function EstimatePage({ onAskAI }) {
  const [form, setForm] = useState({
    area_sqft: 1200,
    city: "Nagpur",
    quality: "standard",
    floors: 1,
  });

  const [boqData, setBoqData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("materials"); // 'materials' | 'stages' | 'labor'

  const fetchBOQ = async () => {
    setLoading(true);
    try {
      const res = await boqAPI.calculateBOQ({
        area_sqft: parseFloat(form.area_sqft) || 1000,
        floors: parseInt(form.floors) || 1,
        city: form.city,
        quality: form.quality,
      });
      setBoqData(res.data);
    } catch (err) {
      console.error("BOQ error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBOQ();
  }, [form.area_sqft, form.city, form.quality, form.floors]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleExportCSV = () => {
    if (!boqData) return;
    let csv = "Item,Quantity,Unit Rate,Estimated Cost (INR),Cost (Lakhs),Share %\n";
    boqData.materials.forEach((m) => {
      csv += `"${m.item}","${m.quantity}","${m.unit_rate}",${m.cost_inr},${m.cost_lakhs},${m.share_pct}%\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `NirmanAI_BOQ_${form.city}_${form.area_sqft}sqft.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const qi = QUALITY_INFO[form.quality] || QUALITY_INFO.standard;

  return (
    <div className="tool-page-container">
      <div className="page-header">
        <div className="header-badge">💰 MATERIAL TAKEOFF &amp; BILL OF QUANTITIES</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <h1>Construction Cost &amp; BOQ Estimator 🏗️</h1>
            <p>Select area, city, and quality to get cement bags, steel tonnes, sand brass, and stage-wise budget estimations.</p>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="action-pill-btn" onClick={handleExportCSV}>
              📥 Export BOQ (CSV)
            </button>
            <button className="action-pill-btn" onClick={() => window.print()}>
              🖨️ Print BOQ Report
            </button>
          </div>
        </div>
      </div>

      <div className="estimate-grid-layout">
        {/* Left Column: Interactive Sliders & Configuration */}
        <div className="card config-card">
          <h2 className="card-title">⚙️ Project Specifications</h2>

          {/* Area Slider */}
          <div className="slider-group">
            <div className="slider-label-row">
              <label>Plot / Carpet Area (sqft):</label>
              <span className="slider-value-badge">{form.area_sqft} sq ft</span>
            </div>
            <input
              type="range"
              min="300"
              max="10000"
              step="50"
              name="area_sqft"
              value={form.area_sqft}
              onChange={handleChange}
              className="custom-range-slider"
            />
            <div className="slider-minmax">
              <span>300 sqft</span>
              <span>2,000 sqft</span>
              <span>10,000 sqft</span>
            </div>
          </div>

          {/* Floors Selection */}
          <div className="form-group-block">
            <label className="field-label">Number of Floors:</label>
            <div className="floors-pill-selector">
              {[
                { val: 1, label: "G (1 Floor)" },
                { val: 2, label: "G+1 (2 Floors)" },
                { val: 3, label: "G+2 (3 Floors)" },
                { val: 4, label: "G+3 (4 Floors)" },
              ].map((fl) => (
                <button
                  key={fl.val}
                  type="button"
                  className={`floor-pill-btn ${parseInt(form.floors) === fl.val ? "active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, floors: fl.val }))}
                >
                  {fl.label}
                </button>
              ))}
            </div>
          </div>

          {/* City Selection */}
          <div className="form-group-block">
            <label className="field-label">City / Location:</label>
            <select className="form-select-styled" name="city" value={form.city} onChange={handleChange}>
              {CITIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Quality Tier Selector */}
          <div className="form-group-block">
            <label className="field-label">Construction Quality Tier:</label>
            <div className="quality-cards-selector">
              {Object.entries(QUALITY_INFO).map(([key, info]) => (
                <div
                  key={key}
                  className={`quality-option-card ${form.quality === key ? "active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, quality: key }))}
                >
                  <div className="q-opt-header">
                    <span className="q-opt-title" style={{ color: info.color }}>
                      {info.label}
                    </span>
                    <span className="q-opt-rate">{info.rate}</span>
                  </div>
                  <div className="q-opt-desc">{info.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {onAskAI && boqData && (
            <button
              className="action-ask-ai-btn"
              onClick={() =>
                onAskAI(
                  `Provide cost optimization and contractor negotiation tips for ${form.quality} quality construction of ${form.area_sqft} sqft (${form.floors} floor) in ${form.city}.`
                )
              }
            >
              💬 Ask AI How To Negotiate &amp; Save Cost
            </button>
          )}
        </div>

        {/* Right Column: Financial Highlights & Detailed BOQ Breakdown */}
        <div className="boq-results-column">
          {boqData && (
            <>
              {/* Financial Hero KPI Banner */}
              <div className="boq-hero-banner">
                <div className="hero-kpi-block main-kpi">
                  <div className="kpi-tag">TOTAL ESTIMATED BUDGET</div>
                  <div className="kpi-big-num">₹{boqData.financial_summary.total_cost_lakhs} Lakhs</div>
                  <div className="kpi-subtext">₹{boqData.financial_summary.rate_per_sqft} / sq ft Built-up Area</div>
                </div>

                <div className="hero-kpi-sub-grid">
                  <div className="sub-kpi-card">
                    <div className="sub-label">🧱 Material Share (55%)</div>
                    <div className="sub-val">₹{boqData.financial_summary.material_cost_lakhs}L</div>
                  </div>
                  <div className="sub-kpi-card">
                    <div className="sub-label">🔨 Labour Share (32%)</div>
                    <div className="sub-val">₹{boqData.financial_summary.labor_cost_lakhs}L</div>
                  </div>
                  <div className="sub-kpi-card">
                    <div className="sub-label">🛡️ Overhead &amp; PM (13%)</div>
                    <div className="sub-val">₹{boqData.financial_summary.contractor_overhead_lakhs}L</div>
                  </div>
                  <div className="sub-kpi-card">
                    <div className="sub-label">⏳ Est. Project Duration</div>
                    <div className="sub-val">{boqData.estimated_duration_months} Months</div>
                  </div>
                </div>
              </div>

              {/* Visual Multi-Color Cost Share Bar */}
              <div className="cost-split-bar-card">
                <div className="cost-bar-header">
                  <span>Cost Distribution Breakdown:</span>
                  <span>Materials 55% | Labour 32% | Contractor Profit 13%</span>
                </div>
                <div className="multi-progress-bar">
                  <div className="bar-segment material-seg" style={{ width: "55%" }} title="Materials (55%)" />
                  <div className="bar-segment labor-seg" style={{ width: "32%" }} title="Labour (32%)" />
                  <div className="bar-segment overhead-seg" style={{ width: "13%" }} title="Overhead (13%)" />
                </div>
              </div>

              {/* Tab Navigation (Materials vs Stages vs Labour) */}
              <div className="boq-tabs-nav">
                <button
                  className={`boq-tab-btn ${activeTab === "materials" ? "active" : ""}`}
                  onClick={() => setActiveTab("materials")}
                >
                  🧱 Material Takeoff ({boqData.materials.length} Items)
                </button>
                <button
                  className={`boq-tab-btn ${activeTab === "stages" ? "active" : ""}`}
                  onClick={() => setActiveTab("stages")}
                >
                  ⏳ 8 Construction Stages &amp; Cashflow
                </button>
                <button
                  className={`boq-tab-btn ${activeTab === "labor" ? "active" : ""}`}
                  onClick={() => setActiveTab("labor")}
                >
                  👷 Labour Days Requirement
                </button>
              </div>

              {/* Tab 1: Material Quantities Takeoff */}
              {activeTab === "materials" && (
                <div className="card tab-content-card">
                  <div className="table-responsive">
                    <table className="custom-data-table">
                      <thead>
                        <tr>
                          <th>Material / Item Description</th>
                          <th>Exact Quantity Takeoff</th>
                          <th>Benchmark Rate</th>
                          <th>Estimated Cost</th>
                          <th>Share %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boqData.materials.map((m, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>{m.item}</strong>
                            </td>
                            <td>
                              <span className="qty-badge">{m.quantity}</span>
                            </td>
                            <td className="rate-cell">{m.unit_rate}</td>
                            <td className="amount-cell">₹{m.cost_lakhs} Lakhs</td>
                            <td>
                              <div className="share-mini-wrap">
                                <span>{m.share_pct}%</span>
                                <div className="share-mini-track">
                                  <div className="share-mini-fill" style={{ width: `${Math.min(m.share_pct * 4, 100)}%` }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 2: Construction Stages & Timeline */}
              {activeTab === "stages" && (
                <div className="card tab-content-card">
                  <div className="table-responsive">
                    <table className="custom-data-table">
                      <thead>
                        <tr>
                          <th>Construction Stage</th>
                          <th>Duration</th>
                          <th>Budget Split (₹)</th>
                          <th>Key IS Quality Inspection Checklist</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boqData.stages.map((st, sIdx) => (
                          <tr key={sIdx}>
                            <td>
                              <strong>{st.stage}</strong>
                            </td>
                            <td>{st.duration_weeks} Weeks</td>
                            <td className="amount-cell">
                              ₹{(st.cost_inr / 100000).toFixed(2)}L <span className="pct-sub">({st.pct}%)</span>
                            </td>
                            <td className="check-cell">{st.key_check}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 3: Labour Requirements */}
              {activeTab === "labor" && (
                <div className="card tab-content-card">
                  <div className="labor-grid">
                    <div className="labor-stat-box">
                      <div className="labor-role">👷 Skilled Masons</div>
                      <div className="labor-days">{boqData.labor_breakdown.mason_days} Days</div>
                      <div className="labor-note">For brickwork, plaster, footing casting</div>
                    </div>
                    <div className="labor-stat-box">
                      <div className="labor-role">🤝 Helpers / Unskilled Beldar</div>
                      <div className="labor-days">{boqData.labor_breakdown.helper_days} Days</div>
                      <div className="labor-note">Material shifting, mortar mixing, curing</div>
                    </div>
                    <div className="labor-stat-box">
                      <div className="labor-role">🔗 Steel Barbenders</div>
                      <div className="labor-days">{boqData.labor_breakdown.barbender_days} Days</div>
                      <div className="labor-note">Cutting, bending, binding column/slab rebar</div>
                    </div>
                    <div className="labor-stat-box">
                      <div className="labor-role">🪵 Shuttering Carpenters</div>
                      <div className="labor-days">{boqData.labor_breakdown.carpenter_days} Days</div>
                      <div className="labor-note">Formwork, props, slab centering</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
